import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { DivisionAnalysisMap, type DivisionMarker } from "@/components/DivisionAnalysisMap";
import { UnitSearch } from "@/components/UnitSearch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, X, Save } from "lucide-react";
import type { DivisionAnalysis } from "@/lib/db/division-analysis";
import type { Division } from "@/lib/types/deck-builder";
import type { Unit } from "@/lib/types/unit";
import { useDataStore } from "@/lib/store/data-store";

export function DivisionAnalysisEditor() {
  const { descriptor: routeDescriptor } = useParams<{ descriptor?: string }>();
  const [searchParams] = useSearchParams();
  const queryDescriptor = searchParams.get("division");
  const descriptor = routeDescriptor || queryDescriptor || undefined;
  const navigate = useNavigate();
  const { units, divisions } = useDataStore();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [coordinatePlacementMode, setCoordinatePlacementMode] = useState(false);
  const [analysis, setAnalysis] = useState<Partial<DivisionAnalysis>>({
    divisionDescriptor: descriptor || "",
    description: "",
    history: null,
    pros: [],
    cons: [],
    unitDescriptors: [],
    mapX: undefined,
    mapY: undefined,
  });

  const resetAnalysisForDescriptor = (targetDescriptor: string) => {
    setAnalysis({
      divisionDescriptor: targetDescriptor,
      description: "",
      history: null,
      pros: [],
      cons: [],
      unitDescriptors: [],
      mapX: undefined,
      mapY: undefined,
    });
  };

  const loadAnalysisForDescriptor = async (targetDescriptor: string) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/division-analysis/${encodeURIComponent(targetDescriptor)}`);

      if (response.ok) {
        const data = await response.json();
        setAnalysis({
          ...data,
          mapX: data.mapX ?? undefined,
          mapY: data.mapY ?? undefined,
        });
      } else if (response.status === 404) {
        resetAnalysisForDescriptor(targetDescriptor);
      } else {
        // Unexpected error (e.g. 500). Reset so we don't keep stale data around.
        console.error(`Failed to load division analysis (${response.status})`);
        resetAnalysisForDescriptor(targetDescriptor);
      }
    } catch (error) {
      console.error("Failed to load analysis:", error);
      resetAnalysisForDescriptor(targetDescriptor);
    } finally {
      setLoading(false);
      setIsDirty(false);
    }
  };

  // Load existing analysis if editing.
  useEffect(() => {
    if (!descriptor) return;
    void loadAnalysisForDescriptor(descriptor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptor]);

  const handleSave = async () => {
    if (!analysis.divisionDescriptor || !analysis.description) {
      alert("Please fill in division descriptor and description");
      return;
    }

    try {
      setSaving(true);
      const response = await fetch("/api/division-analysis", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(analysis),
      });

      if (response.ok) {
        const saved = await response.json();
        navigate(
          `/divisions?division=${encodeURIComponent(saved.divisionDescriptor)}&tab=community`
        );
      } else {
        const error = await response.json();
        alert(`Failed to save: ${error.error}`);
      }
    } catch (error) {
      console.error("Failed to save:", error);
      alert("Failed to save division analysis");
    } finally {
      setSaving(false);
    }
  };

  const addPro = () => {
    setIsDirty(true);
    setAnalysis((prev) => ({
      ...prev,
      pros: [...(prev.pros || []), { id: Date.now(), bulletpoint: "" }],
    }));
  };

  const removePro = (id: number) => {
    setIsDirty(true);
    setAnalysis((prev) => ({
      ...prev,
      pros: (prev.pros || []).filter((p) => p.id !== id),
    }));
  };

  const updatePro = (id: number, bulletpoint: string) => {
    setIsDirty(true);
    setAnalysis((prev) => ({
      ...prev,
      pros: (prev.pros || []).map((p) => (p.id === id ? { ...p, bulletpoint } : p)),
    }));
  };

  const addCon = () => {
    setIsDirty(true);
    setAnalysis((prev) => ({
      ...prev,
      cons: [...(prev.cons || []), { id: Date.now(), bulletpoint: "" }],
    }));
  };

  const removeCon = (id: number) => {
    setIsDirty(true);
    setAnalysis((prev) => ({
      ...prev,
      cons: (prev.cons || []).filter((c) => c.id !== id),
    }));
  };

  const updateCon = (id: number, bulletpoint: string) => {
    setIsDirty(true);
    setAnalysis((prev) => ({
      ...prev,
      cons: (prev.cons || []).map((c) => (c.id === id ? { ...c, bulletpoint } : c)),
    }));
  };

  // Convert unitDescriptors to Unit objects for UnitSearch
  const selectedUnits = useMemo(() => {
    if (!units || !analysis.unitDescriptors) return [];
    return analysis.unitDescriptors
      .map((ud) => units.find((u) => u.descriptorName === ud.descriptorId))
      .filter((u): u is Unit => u !== undefined);
  }, [units, analysis.unitDescriptors]);

  // Find the unitDescriptor id for a given unit
  const getUnitDescriptorId = (unit: Unit): number | undefined => {
    return analysis.unitDescriptors?.find((ud) => ud.descriptorId === unit.descriptorName)?.id;
  };

  const handleUnitSelect = (unit: Unit) => {
    setIsDirty(true);
    setAnalysis((prev) => ({
      ...prev,
      unitDescriptors: [...(prev.unitDescriptors || []), { id: Date.now(), descriptorId: unit.descriptorName }],
    }));
  };

  const handleUnitRemove = (unit: Unit) => {
    const descriptorId = getUnitDescriptorId(unit);
    if (descriptorId !== undefined) {
      setIsDirty(true);
      setAnalysis((prev) => ({
        ...prev,
        unitDescriptors: (prev.unitDescriptors || []).filter((u) => u.id !== descriptorId),
      }));
    }
  };

  const handleCoordinateClick = (x: number, y: number) => {
    setIsDirty(true);
    setAnalysis((prev) => ({
      ...prev,
      mapX: x,
      mapY: y,
    }));
  };

  // Build markers for map
  const markers: DivisionMarker[] =
    analysis.mapX !== undefined && analysis.mapX !== null && 
    analysis.mapY !== undefined && analysis.mapY !== null
      ? [
          {
            divisionDescriptor: analysis.divisionDescriptor || "",
            x: analysis.mapX,
            y: analysis.mapY,
          },
        ]
      : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="px-4 py-2">
        <h1 className="text-3xl font-bold">Edit Division Analysis</h1>
      </div>
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[40%_60%] gap-3 px-4 py-2 overflow-hidden">
        {/* Left side: Form */}
        <div className="flex flex-col gap-3 overflow-auto">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label htmlFor="division">Division</Label>
                <Select
                  value={analysis.divisionDescriptor || ""}
                  onValueChange={(value) => {
                    const nextDescriptor = value;
                    const currentDescriptor = analysis.divisionDescriptor || "";
                    if (nextDescriptor === currentDescriptor) return;

                    if (isDirty) {
                      const confirmed = window.confirm(
                        "You have unsaved changes. Discard them and load the selected division?"
                      );
                      if (!confirmed) return;
                    }

                    void loadAnalysisForDescriptor(nextDescriptor);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a division" />
                  </SelectTrigger>
                  <SelectContent>
                    {(divisions || []).map((div) => (
                      <SelectItem key={div.descriptor} value={div.descriptor}>
                        {div.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={analysis.description || ""}
                    onChange={(e) => {
                      setIsDirty(true);
                      setAnalysis((prev) => ({ ...prev, description: e.target.value }));
                    }}
                  rows={6}
                />
              </div>

              <div>
                <Label htmlFor="history">History (optional)</Label>
                <Textarea
                  id="history"
                  value={analysis.history ?? ""}
                    onChange={(e) => {
                      setIsDirty(true);
                      setAnalysis((prev) => ({ ...prev, history: e.target.value || null }));
                    }}
                  rows={4}
                />
              </div>

              <div>
                <Label>Map Coordinates</Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label htmlFor="mapX" className="text-xs">X</Label>
                    <Input
                      id="mapX"
                      type="number"
                      value={analysis.mapX ?? ""}
                        onChange={(e) => {
                          setIsDirty(true);
                          setAnalysis((prev) => ({
                            ...prev,
                            mapX: e.target.value ? parseInt(e.target.value) : undefined,
                          }));
                        }}
                    />
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="mapY" className="text-xs">Y</Label>
                    <Input
                      id="mapY"
                      type="number"
                      value={analysis.mapY ?? ""}
                        onChange={(e) => {
                          setIsDirty(true);
                          setAnalysis((prev) => ({
                            ...prev,
                            mapY: e.target.value ? parseInt(e.target.value) : undefined,
                          }));
                        }}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Click on the map to set coordinates
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Pros</CardTitle>
                <Button variant="outline" size="sm" onClick={addPro}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {(analysis.pros || []).map((pro) => (
                <div key={pro.id} className="flex gap-1.5">
                  <Input
                    value={pro.bulletpoint}
                    onChange={(e) => updatePro(pro.id, e.target.value)}
                    placeholder="Enter pro bulletpoint"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removePro(pro.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Cons</CardTitle>
                <Button variant="outline" size="sm" onClick={addCon}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {(analysis.cons || []).map((con) => (
                <div key={con.id} className="flex gap-1.5">
                  <Input
                    value={con.bulletpoint}
                    onChange={(e) => updateCon(con.id, e.target.value)}
                    placeholder="Enter con bulletpoint"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeCon(con.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Unit Descriptors</CardTitle>
            </CardHeader>
            <CardContent>
              <UnitSearch
                onSelect={handleUnitSelect}
                selectedUnits={selectedUnits}
                showImages={true}
                showSelectedUnits={true}
                onRemove={handleUnitRemove}
                placeholder="Search for unit to add..."
              />
            </CardContent>
          </Card>

          <div className="flex gap-2 pb-3">
            <Button onClick={handleSave} disabled={saving} className="flex-1">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/divisions?tab=community")}
            >
              Cancel
            </Button>
          </div>
        </div>

        {/* Right side: Map */}
        <div className="flex-1 min-h-0">
          <Card className="h-full">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Map</CardTitle>
                <div className="flex items-center gap-1.5">
                  <Label htmlFor="coordinate-mode" className="text-sm">
                    Place Coordinates
                  </Label>
                  <input
                    id="coordinate-mode"
                    type="checkbox"
                    checked={coordinatePlacementMode}
                    onChange={(e) => setCoordinatePlacementMode(e.target.checked)}
                    className="h-4 w-4"
                  />
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {coordinatePlacementMode 
                  ? "Click on the map to set coordinates" 
                  : "Drag to pan, scroll to zoom. Enable 'Place Coordinates' to set coordinates by clicking."}
              </p>
            </CardHeader>
            <CardContent className="p-0 h-[calc(100%-6rem)]">
              <DivisionAnalysisMap
                markers={markers}
                onCoordinateClick={handleCoordinateClick}
                showCoordinates={true}
                editMode={true}
                coordinatePlacementMode={coordinatePlacementMode}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

